import { searchClient } from "algolia-instant-search";
import { RT_PRODUCT_FEED_FILTERS } from "@theme/algolia-filter-builder";
const { autocomplete, getAlgoliaResults } = window["@algolia/autocomplete-js"];

function debouncePromise(fn, time) {
  let timer = undefined;

  return function debounced(...args) {
    if (timer) {
      clearTimeout(timer);
    }

    return new Promise((resolve) => {
      timer = setTimeout(() => resolve(fn(...args)), time);
    });
  };
}

const DEBOUNCE_MS = 300;
const STALL_THRESHOLD_MS = 500;
const debounced = debouncePromise(
  (items) => Promise.resolve(items),
  DEBOUNCE_MS,
);

if (!customElements.get("algolia-autocomplete")) {
  class AlgoliaAutocomplete extends HTMLElement {
    initialized = false;
    catalogIds = this.dataset?.catalogIds?.split(",") ?? [];
    isB2B = this.dataset.b2b === "true";
    abortController = new AbortController();

    connectedCallback() {
      if (!this.initialized) this.init.call(this);

      this.resizeObserver = new ResizeObserver(() => {
        this.autocompleteInstance?.setIsOpen(false);
      });
      this.resizeObserver.observe(this.firstElementChild);

      document.addEventListener(
        "pointerdown",
        this.handleClickAway.bind(this),
        {
          signal: this.abortController.signal,
        },
      );
    }

    handleClickAway(e) {
      if (!this.contains(e.target)) this.autocompleteInstance?.setIsOpen(false);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.abortController?.abort();
    }

    init() {
      this.autocompleteInstance = autocomplete({
        container: this,
        panelContainer: this,
        placeholder: this.dataset?.placeholder ?? "Search products...",
        debug: false,
        openOnFocus: false,
        detachedMediaQuery: "none",
        stallThreshold: STALL_THRESHOLD_MS,
        onSubmit: ({ state }) => {
          window.location.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(state.query)}`;
        },
        classNames: {
          root: "algolia-autocomplete-root",
          form: "algolia-autocomplete-form",
          inputWrapper: "",
          inputWrapperPrefix: "",
          inputWrapperSuffix: "",
          label: "algolia-autocomplete-label",
          input: "algolia-autocomplete-input",
          submitButton: "algolia-autocomplete-submit",
          clearButton: "",
          loadingIndicator: "",
          panel: "algolia-autocomplete-panel predictive-search-results",
          panelLayout: "",
          source: "",
          sourceHeader: "",
          sourceFooter: "",
          sourceNoResults: "",
          list: "",
          item: "algolia-autocomplete-item",
        },
        getSources: ({ query }) => {
          return debounced([
            this.getSuggestionsSource(query),
            this.getProductsSource(query),
            this.getCollectionSource(query),
            this.getPageSource(query),
          ]);
        },
      });

      this.initialized = true;
    }

    getSuggestionsSource(query) {
      return {
        sourceId: "suggestions",
        getItems() {
          return getAlgoliaResults({
            searchClient,
            queries: [
              {
                indexName: "shopify_racetech_query_suggestions",
                params: {
                  query,
                  hitsPerPage: 4,
                  attributesToRetrieve: ["query"],
                  attributesToSnippet: ["query:10"],
                  snippetEllipsisText: "…",
                },
              },
            ],
          });
        },
        templates: {
          header({ html }) {
            return html` <h3>Suggestions</h3> `;
          },
          item({ item, components, html }) {
            const searchUrl = `/search?q=${encodeURIComponent(item.query)}`;

            return html`<a class="block" href="${searchUrl}">
              ${components.Highlight({
                hit: item,
                attribute: "query",
              })}
            </a>`;
          },
        },
      };
    }

    getProductsSource(query) {
      const catalogIdsFilter = this.catalogIds[0]
        ? `AND catalog_ids:${this.catalogIds[0]}`
        : "";
      const rtProductFeedFilter = this.isB2B
        ? RT_PRODUCT_FEED_FILTERS.b2b
        : RT_PRODUCT_FEED_FILTERS.retail;

      return {
        sourceId: "products",
        getItems() {
          return getAlgoliaResults({
            searchClient,
            queries: [
              {
                indexName: "shopify_products_manual_index_prod",
                params: {
                  query,
                  typoTolerance: "min",
                  hitsPerPage: 80,
                  distinct: true,
                  filters: `status:ACTIVE AND NOT tags:"builder-backing" AND ${rtProductFeedFilter} ${catalogIdsFilter}`,
                  attributesToRetrieve: [
                    "handle",
                    "title",
                    "featured_image",
                    "sku",
                    "variants",
                  ],
                  attributesToSnippet: ["title:10", "description:35"],
                  snippetEllipsisText: "…",
                },
              },
            ],
            transformResponse({ hits, results }) {
              return hits.map((hitsArray, idx) =>
                hitsArray.map((hit) => ({
                  ...hit,
                  queryID: results[idx].queryID,
                })),
              );
            },
          });
        },
        templates: {
          header({ html }) {
            return html` <h3>Products</h3> `;
          },
          item({ item, components, html }) {
            const sku = item.sku || item.variants?.[0]?.sku || "";
            const algoliaQueryIdPrarm = item.queryID
              ? `?algolia_query_id=${item.queryID}`
              : "";

            return html`<div
              role="option"
              class="predictive-result-item result-hover"
            >
              <a href="/products/${item.handle}${algoliaQueryIdPrarm}">
                <div class="autocomplete-item-row">
                  <div class="item-image-preview">
                    ${item.featured_image
                      ? html`<img
                          src="${item.featured_image.url}"
                          alt="${item.title}"
                        />`
                      : html`<div class="image-preview no-image">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 525.5 525.5"
                          >
                            <path
                              d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"
                            />
                            <path
                              d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-16c1.2-1 3.1-.9 4.2.1l7.7 7.2 26.4-20.9c1.1-.9 2.8-.8 3.8.3l9.7 11.4v19.2h-71.7v-1.3zm81.4-60.7c4.5 0 8.1 3.7 8.1 8.1s-3.6 8.1-8.1 8.1c-4.5 0-8.1-3.7-8.1-8.1s3.7-8.1 8.1-8.1z"
                            />
                          </svg>
                        </div>`}
                  </div>
                  <div class="item-detail">
                    <h5 class="h5 item-title">
                      <p class="font-body">
                        ${components.Highlight({
                          hit: item,
                          attribute: "title",
                        })}
                      </p>
                    </h5>
                    ${sku
                      ? html`<p class="autocomplete-sku">SKU: ${sku}</p>`
                      : ""}
                  </div>
                </div>
              </a>
            </div>`;
          },
        },
      };
    }

    getCollectionSource(query) {
      return {
        sourceId: "collections",
        getItems() {
          return getAlgoliaResults({
            searchClient,
            queries: [
              {
                indexName: "shopify_collections_manual_index_prod",
                params: {
                  query,
                  hitsPerPage: 5,
                  attributesToRetrieve: ["handle", "title"],
                  attributesToSnippet: ["title:10"],
                  snippetEllipsisText: "…",
                  filters: "metafield_seo_hidden:false",
                },
              },
            ],
          });
        },
        templates: {
          header({ html }) {
            return html` <h3>Collections</h3> `;
          },
          item({ item, components, html }) {
            return html`
              <a href=${"/collections/" + item.handle} class="block">
                ${components.Highlight({
                  hit: item,
                  attribute: "title",
                })}
              </a>
            `;
          },
        },
      };
    }

    getPageSource(query) {
      return {
        sourceId: "pages",
        getItems() {
          return getAlgoliaResults({
            searchClient,
            queries: [
              {
                indexName: "shopify_pages_manual_index_prod",
                params: {
                  query,
                  hitsPerPage: 5,
                  attributesToRetrieve: ["handle", "title"],
                  attributesToSnippet: ["title:10"],
                  snippetEllipsisText: "…",
                },
              },
            ],
          });
        },
        templates: {
          header({ html }) {
            return html` <h3>Pages</h3> `;
          },
          item({ item, components, html }) {
            return html`
              <a href="/pages/${item.handle}" class="block">
                ${components.Highlight({
                  hit: item,
                  attribute: "title",
                })}
              </a>
            `;
          },
        },
      };
    }
  }
  customElements.define("algolia-autocomplete", AlgoliaAutocomplete);
}
